import { auth } from '../../firebase';
import { apiConfig } from '../../config';

// --- 1. Define Request/Response Types ---

// Document source from RAG search
export interface DocumentSource {
  title: string;
  gcs_uri: string;
  snippet?: string;
}

// Chat message structure (exported for use in ChatCoPilot component)
export interface ChatMessage {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  documentSources?: DocumentSource[];
}

// API response structure (exported for use in ChatCoPilot component)
export interface CopilotApiResponse {
  success: boolean;
  response: ChatMessage;
  documentSources?: DocumentSource[];
  error?: string;
}

// Payload to send to backend
interface QueryRequest {
  message: string;
  history: ChatMessage[];
}

// Payload received from backend
interface QueryResponse {
  success: boolean;
  response: ChatMessage;
  document_sources?: DocumentSource[];
  error?: string;
}

// --- 2. Get Backend URL from centralized config ---
const API_URL = apiConfig.copilotUrl;

// --- 3. Get user email (consistent with insightsApi approach) ---
const getUserEmail = (): string => {
  // Try localStorage first (set on login)
  const storedEmail = localStorage.getItem('user_email');
  if (storedEmail) return storedEmail;
  
  // Fallback to Firebase auth state
  const firebaseEmail = auth.currentUser?.email;
  if (firebaseEmail) return firebaseEmail;
  
  // Final fallback
  return '';
};

// --- 4. Live API Function ---
export const postQuery = async (
  message: string,
  history: ChatMessage[]
): Promise<CopilotApiResponse> => {
  try {
    const userEmail = getUserEmail();
    
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-email': userEmail,
      },
      body: JSON.stringify({
        message,
        // Send simplified history to save tokens/bandwidth
        history: history.map(msg => ({
          id: msg.id,
          sender: msg.sender,
          text: msg.text
        }))
      }),
    });

    if (!response.ok) {
      // Handle HTTP errors (404, 500, etc.)
      const errorText = await response.text();
      console.error('API Error:', response.status, errorText);
      const errorMessage = `Server Error (${response.status}): ${errorText || 'The backend service encountered an error.'}`;
      return {
        success: false,
        error: errorMessage,
        response: {
          id: `error-${Date.now()}`,
          sender: 'ai',
          text: errorMessage
        }
      };
    }

    // Parse the successful JSON response
    const data: QueryResponse = await response.json();
    
    if (data.success) {
        // Include document sources in the response message
        const responseWithSources: ChatMessage = {
            ...data.response,
            documentSources: data.document_sources || undefined
        };
        return {
            success: true,
            response: responseWithSources,
            documentSources: data.document_sources
        };
    } else {
        // Include error at top level AND in response for flexible handling
        const errorMessage = data.error || "An unknown error occurred in the AI service.";
        return {
            success: false,
            error: errorMessage,
            response: {
                id: `error-${Date.now()}`,
                sender: 'ai',
                text: errorMessage
            }
        }
    }

  } catch (error) {
    console.error('Network Error:', error);
    return {
      success: false,
      response: {
        id: `error-${Date.now()}`,
        sender: 'ai',
        text: 'Network Error: Could not connect to the Co-Pilot service. Please check your internet connection or VPN.'
      }
    };
  }
};

// --- 5. Get Signed URL for Document Download ---
export const getDocumentSignedUrl = async (gcsUri: string): Promise<{
  success: boolean;
  signedUrl?: string;
  fileName?: string;
  error?: string;
}> => {
  try {
    const userEmail = getUserEmail();
    const baseUrl = apiConfig.baseUrl;
    
    const response = await fetch(
      `${baseUrl}/api/document/signed-url?gcs_uri=${encodeURIComponent(gcsUri)}`,
      {
        method: 'GET',
        headers: {
          'x-user-email': userEmail,
        },
      }
    );

    if (!response.ok) {
      return { success: false, error: `HTTP ${response.status}` };
    }

    const data = await response.json();
    
    if (data.success) {
      return {
        success: true,
        signedUrl: data.data.signed_url,
        fileName: data.data.file_name
      };
    } else {
      return { success: false, error: data.error };
    }
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return { success: false, error: 'Failed to generate download link' };
  }
};
